import { incomingWins } from "@founderdeck/core";
import type { DayLog, Note, Project, Task } from "./api";
import {
  deleteRecord,
  getRecord,
  metaGet,
  metaSet,
  outboxAll,
  outboxRemove,
  putRecord,
  type Entity,
} from "./idb";

interface SyncResponse {
  now: number;
  tasks: Task[];
  projects: Project[];
  dayLogs: DayLog[];
  notes: Note[];
  deletions: { entity: Entity; id: string }[];
}

let running = false;
let again = false;

const parse = (iso: string | null | undefined): number | null =>
  iso ? Date.parse(iso) : null;

/**
 * Push the outbox and pull remote changes, reconciling the local IndexedDB
 * mirror by last-write-wins. Returns true if anything changed locally (so the
 * caller can refresh queries). Safe to call often; concurrent calls coalesce.
 */
export async function doSync(): Promise<boolean> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return false;
  if (running) {
    again = true;
    return false;
  }
  running = true;
  let changed = false;
  try {
    do {
      again = false;
      const ops = await outboxAll();
      const since = Number((await metaGet("lastSyncAt")) ?? "0");
      const payloadOps = ops.map((o) =>
        o.type === "upsert"
          ? { entity: o.entity, type: "upsert", id: o.id, updatedAt: o.ts, data: o.data ?? {} }
          : { entity: o.entity, type: "delete", id: o.id, deletedAt: o.ts },
      );

      const res = await fetch("/api/sync", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ since, ops: payloadOps }),
      });
      if (!res.ok) return changed;
      const data = (await res.json()) as SyncResponse;

      await applyPull(data);
      const opIds = ops.map((o) => o.opId).filter((x): x is number => x != null);
      if (opIds.length) await outboxRemove(opIds);
      await metaSet("lastSyncAt", String(data.now));

      if (
        ops.length ||
        data.tasks.length ||
        data.projects.length ||
        data.dayLogs.length ||
        data.notes.length ||
        data.deletions.length
      ) {
        changed = true;
      }
    } while (again);
    return changed;
  } catch {
    return false;
  } finally {
    running = false;
  }
}

async function mergeOne(entity: Entity, incoming: { id: string; updatedAt: string }) {
  const local = await getRecord<{ updatedAt: string }>(entity, incoming.id);
  if (incomingWins(Date.parse(incoming.updatedAt), parse(local?.updatedAt))) {
    await putRecord(entity, incoming);
  }
}

async function applyPull(data: SyncResponse): Promise<void> {
  for (const t of data.tasks) await mergeOne("task", t);
  for (const p of data.projects) await mergeOne("project", p);
  for (const d of data.dayLogs) await mergeOne("dayLog", d);
  for (const n of data.notes) await mergeOne("note", n);
  for (const del of data.deletions) await deleteRecord(del.entity, del.id);
}
